import { computed } from "mobx";
import { DocumentContentModelWithTileDragging } from "./drag-tiles";
import { highlightTargetKey, resolveHighlightReference, sameHighlightReference } from "../highlights/highlight-reference";
export const DocumentContentModelWithHighlights = DocumentContentModelWithTileDragging
    .named("DocumentContentModelWithHighlights")
    .volatile(self => ({
    hoveredHighlightRef: undefined,
    pinnedHighlightRef: undefined,
    // Which source set each ref. A reference cannot identify its own source — two sources can
    // cite the same object, and a variable reference names no source at all — so a source that
    // wants to release only what it set has to be told apart by something else.
    //
    // A source with a lifetime must set a token and release on unmount; see docs/highlights.md,
    // which is the authority on the rules. Leaving it unset means nobody can own-release the
    // highlight, so it is for a caller that sets a reference and never needs to take it back.
    hoveredHighlightSource: undefined,
    pinnedHighlightSource: undefined,
}))
    .views(self => ({
    /**
     * Exactly one reference is highlighted at a time. Hover REPLACES pin rather than adding to
     * it: while previewing you want to see what you are previewing, and on mouse-out this
     * reverts to the pinned reference rather than clearing.
     */
    get highlightRef() {
        var _a;
        return (_a = self.hoveredHighlightRef) !== null && _a !== void 0 ? _a : self.pinnedHighlightRef;
    },
    get highlightState() {
        if (self.hoveredHighlightRef) {
            // Hovering the reference that is already pinned must not visually downgrade it to
            // "preview" — that would flicker back to "pinned" on mouse-out for no meaningful
            // reason. Rule 1 (hover replaces pin) still applies for any *different* reference.
            if (self.pinnedHighlightRef
                && sameHighlightReference(self.hoveredHighlightRef, self.pinnedHighlightRef)) {
                return "pinned";
            }
            return "preview";
        }
        if (self.pinnedHighlightRef)
            return "pinned";
        return undefined;
    },
}))
    .views(self => {
    // A private closure variable for encapsulation. Keep it that way: a text-range reference kind
    // has no id and cannot be expressed as a tileId/objectId pair, so callers must go through
    // `isObjectHighlighted`/`objectHighlightState` rather than the target collection itself.
    const highlightedTargetKeys = computed(() => {
        const ref = self.highlightRef;
        if (!ref)
            return new Set();
        const targets = resolveHighlightReference(ref, self);
        return new Set(targets.map(target => highlightTargetKey(target.tileId, target.objectId)));
    });
    function isObjectHighlighted(tileId, objectId) {
        if (!tileId || !objectId)
            return false;
        return highlightedTargetKeys.get().has(highlightTargetKey(tileId, objectId));
    }
    return {
        isObjectHighlighted,
        /**
         * Every highlighted object shares one state, because only one reference is highlighted at
         * a time. This can never return "pinned" for one object while returning "preview" for
         * another in the same render.
         */
        objectHighlightState(tileId, objectId) {
            return isObjectHighlighted(tileId, objectId) ? self.highlightState : undefined;
        },
    };
})
    .actions(self => ({
    setHoveredHighlightRef(ref, source) {
        self.hoveredHighlightRef = ref;
        self.hoveredHighlightSource = source;
    },
    clearHoveredHighlightRef() {
        self.hoveredHighlightRef = undefined;
        self.hoveredHighlightSource = undefined;
    },
    setPinnedHighlightRef(ref, source) {
        self.pinnedHighlightRef = ref;
        self.pinnedHighlightSource = source;
    },
    clearPinnedHighlightRef() {
        self.pinnedHighlightRef = undefined;
        self.pinnedHighlightSource = undefined;
    },
}))
    .actions(self => ({
    /**
     * Release a highlight only if this source is the one that set it. Several sources share one
     * document, so a source going away must not clear a highlight another source owns — including
     * when both cite the same thing, which is exactly when comparing references cannot tell them
     * apart.
     */
    clearHoveredHighlightRefIfOwn(source) {
        if (source && self.hoveredHighlightSource === source)
            self.clearHoveredHighlightRef();
    },
    clearPinnedHighlightRefIfOwn(source) {
        if (source && self.pinnedHighlightSource === source)
            self.clearPinnedHighlightRef();
    },
    /**
     * Clicking a source that already owns the pin releases it. Clicking a *different* source
     * takes the pin over rather than releasing it, even when both cite the same thing — otherwise
     * asking to see something a second control already points at would turn it off.
     */
    togglePinnedHighlightRef(ref, source) {
        const ownsPin = self.pinnedHighlightRef
            && sameHighlightReference(self.pinnedHighlightRef, ref)
            && self.pinnedHighlightSource === source;
        if (ownsPin) {
            self.clearPinnedHighlightRef();
        }
        else {
            self.setPinnedHighlightRef(ref, source);
        }
    },
}));
//# sourceMappingURL=data:application/json;base64,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